import { useCallback, useSyncExternalStore } from 'react'

import {
  applyColorScheme,
  storeColorScheme,
  COLOR_SCHEME_STORAGE_KEY,
  type ColorScheme,
} from '#ui/lib/color-scheme.ts'

/**
 * Read and change the light/dark choice from React.
 *
 * **The source of truth is the class on `<html>`, not a piece of React state**, and that is the
 * whole design. The boot shim has already put it there before the first paint — so a snapshot taken
 * from the DOM is, by construction, what the user is currently looking at. A `useState` seeded from
 * `localStorage` would be a second copy of the same fact, and the two disagree for one render on
 * every cold load, which is the flash the shim exists to prevent reintroduced inside React.
 *
 * It also means anything else that sets the class — the shim, another tab, a future settings page —
 * is picked up without this module knowing about it.
 *
 * `useSyncExternalStore` rather than an effect, because this has a server snapshot. The website is
 * server-rendered and the server cannot know the choice, so it renders `system` and React swaps to
 * the real value after hydration. That is the documented purpose of `getServerSnapshot`, not a
 * mismatch.
 */

const listeners = new Set<() => void>()

function emit(): void {
  for (const listener of listeners) listener()
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)

  /**
   * `storage` fires in OTHER tabs, never the one that wrote. An operator with the console open
   * twice should not have to reload the second one to stop it being the wrong color.
   */
  const onStorage = (event: StorageEvent) => {
    if (event.key !== COLOR_SCHEME_STORAGE_KEY) return
    applyColorScheme(readFromStorageEvent(event), globalThis.document.documentElement)
    listener()
  }

  globalThis.addEventListener('storage', onStorage)
  return () => {
    listeners.delete(listener)
    globalThis.removeEventListener('storage', onStorage)
  }
}

/** A removed key arrives as `newValue: null`, which is the `system` case. */
function readFromStorageEvent(event: StorageEvent): ColorScheme {
  return event.newValue === 'dark' || event.newValue === 'light' ? event.newValue : 'system'
}

/** What the page is actually painted as, read off the element the stylesheet keys on. */
function getSnapshot(): ColorScheme {
  const { classList } = globalThis.document.documentElement
  if (classList.contains('dark')) return 'dark'
  if (classList.contains('light')) return 'light'
  return 'system'
}

/** The server cannot know. `system` is the honest answer and the one the markup is built for. */
function getServerSnapshot(): ColorScheme {
  return 'system'
}

export function useColorScheme(): {
  /** `system` means "follow the machine", and is the default. */
  scheme: ColorScheme
  setScheme: (next: ColorScheme) => void
} {
  const scheme = useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot)

  const setScheme = useCallback((next: ColorScheme) => {
    // The class first, so the repaint happens in the same frame as the click; then storage, so it
    // survives a reload; then the notification, so every subscriber re-reads the class rather than
    // being handed a value that might already be stale.
    applyColorScheme(next, globalThis.document.documentElement)
    storeColorScheme(next, globalThis.localStorage)
    emit()
  }, [])

  return { scheme, setScheme }
}
